import { useCallback, useId, useState } from 'react';
import type { MutableRefObject } from 'react';
import { getTableState } from './tableStatePersistence';
import { Icon } from '@iconify/react';
import { Button, Dropdown, DropdownItem, DropdownMenu, DropdownTrigger, Tooltip } from '@nextui-org/react';

const DEFAULT_TRIGGER_ARIA_LABEL = 'Columns';

/** The part of the grid API the chooser uses; fits both the `ag-grid-community` and `@ag-grid-community/core` GridApi. */
type ColumnVisibilityApi = { setColumnVisible: (key: string, visible: boolean) => void };

export function buildTableColumnVisibilitySelection(
  tableId: string,
  columnLabels: readonly string[],
  labelToColId: Record<string, string>,
  hiddenByDefaultLabels: readonly string[] = [],
): Record<string, boolean> {
  const base = columnLabels.reduce<Record<string, boolean>>((acc, name) => {
    acc[name] = !hiddenByDefaultLabels.includes(name);
    return acc;
  }, {});
  const saved = getTableState(tableId);
  if (!saved?.columnState?.length) return base;
  for (const label of columnLabels) {
    const colId = labelToColId[label];
    if (!colId) continue;
    const piece = saved.columnState.find((c) => c.colId === colId);
    if (piece && typeof piece.hide === 'boolean') {
      base[label] = !piece.hide;
    }
  }
  return base;
}

export type TableColumnChooserProps = {
  /** Human-readable labels shown in the menu (mapped to grid colIds via labelToColId). */
  columnLabels: readonly string[];
  gridApiRef: MutableRefObject<ColumnVisibilityApi | null>;
  tableId: string;
  labelToColId: Record<string, string>;
  hiddenByDefaultLabels?: readonly string[];
  formatColumnLabel?: (label: string) => string;
  triggerTooltip?: string;
  triggerId?: string;
};

function columnVisibilityCheckboxId(tableId: string, colId: string): string {
  return `table-column-visibility-${tableId}-${colId}`.replace(/\s+/g, '-');
}

/** Toolbar or standalone control to show/hide AG Grid columns (state persists via tableId). */
export const TableColumnChooser = ({
  columnLabels,
  gridApiRef,
  tableId,
  labelToColId,
  hiddenByDefaultLabels = [],
  formatColumnLabel = (label) => label,
  triggerTooltip,
  triggerId,
}: TableColumnChooserProps) => {
  const [open, setOpen] = useState(false);
  const [visibleByLabel, setVisibleByLabel] = useState<Record<string, boolean>>(() =>
    buildTableColumnVisibilitySelection(tableId, columnLabels, labelToColId, hiddenByDefaultLabels),
  );
  const reactId = useId();

  const toggleColumnVisibility = useCallback(
    (label: string) => {
      setVisibleByLabel((prev) => {
        const nextVisible = !prev[label];
        const colId = labelToColId[label];
        const api = gridApiRef.current;
        if (api && colId) {
          api.setColumnVisible(colId, nextVisible);
        }
        return { ...prev, [label]: nextVisible };
      });
    },
    [gridApiRef, labelToColId],
  );

  const dropdown = (
    <Dropdown isOpen={open} onOpenChange={setOpen}>
      <DropdownTrigger>
        <Button
          id={triggerId}
          size="sm"
          variant="bordered"
          isIconOnly
          aria-label={triggerTooltip ?? DEFAULT_TRIGGER_ARIA_LABEL}
        >
          <Icon icon="proicons:filter" width="1em" height="1em" aria-hidden />
        </Button>
      </DropdownTrigger>
      <DropdownMenu
        aria-label={DEFAULT_TRIGGER_ARIA_LABEL}
        closeOnSelect={false}
        className="min-w-[220px]"
        classNames={{ list: 'max-h-80 overflow-y-auto' }}
      >
        {columnLabels.map((label) => {
          const colId = labelToColId[label];
          const checkboxId = colId
            ? columnVisibilityCheckboxId(tableId, colId)
            : `${reactId}-${label}`;
          const displayLabel = formatColumnLabel(label);
          return (
            <DropdownItem key={label} isReadOnly textValue={displayLabel}>
              <div className="flex items-center gap-2">
                <input
                  id={checkboxId}
                  type="checkbox"
                  checked={!!visibleByLabel[label]}
                  onChange={() => toggleColumnVisibility(label)}
                  aria-label={displayLabel}
                />
                <label htmlFor={checkboxId} className="cursor-pointer">
                  {displayLabel}
                </label>
              </div>
            </DropdownItem>
          );
        })}
      </DropdownMenu>
    </Dropdown>
  );

  if (!triggerTooltip) {
    return dropdown;
  }

  return (
    <Tooltip content={triggerTooltip} placement="top" isDisabled={open}>
      <span className="inline-flex">{dropdown}</span>
    </Tooltip>
  );
}


/** AG Grid `headerComponent` — same menu as {@link TableColumnChooser} in the column header row. */
export const TableColumnChooserGridHeader = (params: TableColumnChooserProps) => {
  return (
    <TableColumnChooser
      columnLabels={params.columnLabels}
      gridApiRef={params.gridApiRef}
      tableId={params.tableId}
      labelToColId={params.labelToColId}
      hiddenByDefaultLabels={params.hiddenByDefaultLabels}
      formatColumnLabel={params.formatColumnLabel}
      triggerTooltip={params.triggerTooltip}
      triggerId={params.triggerId}
    />
  );
}
